import * as React from 'react'

export interface LegalSection {
  number: string
  title: string
  body: React.ReactNode
}

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    number: '1',
    title: 'Personal Information Collected',
    body: (
      <>
        <p>
          When we use the term &ldquo;Personal Information&rdquo; in this Policy, we mean any information
          relating to an identified or identifiable natural person who can be identified, directly or
          indirectly, in particular by reference to an identifier such as a name, an identification number,
          location data, an online identifier or to one or more factors specific to the physical,
          physiological, genetic, mental, economic, cultural or social identity of that natural person. It
          does not include aggregated or anonymized information that is maintained in a form that is not
          capable of being associated with or reasonably linked to you. Our use and sharing of aggregated or
          anonymized information is not covered by this Policy.
        </p>
        <p>
          We collect Personal Information about you in various ways, including directly from you when you
          provide it to us, automatically when you interact with our Services, and from other sources and
          third parties:
        </p>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">Personal Information You Provide</h3>
          <p>
            We collect Personal Information you provide through our Services or give to us in any other way,
            including over the phone, electronically or on paper. In connection with these activities, we
            collect the following:
          </p>
          <ul className="m-0 flex flex-col gap-2 pl-6">
            <li>
              Contact information, such as name, email address, postal address, and telephone number;
              Demographic information, such as age or region;
            </li>
            <li>
              Employment-related information, such as employer, broker, shipper, receiver, truck and driver
              identifiers and work title;
            </li>
            <li>Account information, such as user ID, password and load preferences;</li>
            <li>
              Transaction information, such as payment information, financial account information, credit
              information, order details, and order preferences;
            </li>
            <li>
              Load-Related information, such as load identifiers, price, pick-up and drop-off information;
              Geolocation information, such as your current location and destination preferences; Broker and
              Shipper generated content, such as preferences relating to calls, messages and load negotiation;
            </li>
            <li>
              Inquiry information, such as the content of your email, text, or chat with us and, where
              applicable, your voice from calls with our Services.
            </li>
          </ul>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">Personal Information Automatically Collected</h3>
          <p>
            As is true of most digital platforms, we collect certain Personal Information automatically when
            you use our online Services, including:
          </p>
          <ul className="m-0 flex flex-col gap-2 pl-6">
            <li>
              <strong>Log Files:</strong> We record certain information in a log file every time your browser
              or mobile device requests any information from our server, such as your device&rsquo;s operating
              system, application software, Internet Protocol (IP) address, device identifiers, access times,
              browser type and language, peripherals, user log data, Active Directory information, and the
              websites you visited before and after our online Services.
            </li>
            <li>
              <strong>Cookies, Analytics and Other Data Collection Tools:</strong> We also collect (directly
              or via third parties) information about your usage and activity on our online Services,
              including information about your orders through essential and non-essential cookies, web
              beacons, and similar data collection tools.
            </li>
          </ul>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            Personal Information from Other Sources and Third Parties
          </h3>
          <p>
            We also obtain Personal Information from other sources, which we often combine with Personal
            Information we collect either on our Services or directly from you.
          </p>
          <ul className="m-0 flex flex-col gap-2 pl-6">
            <li>
              <strong>Business Partners and Service Providers:</strong> Our business partners and service
              providers, such as payment processors and analytics providers, collect Personal Information and
              often share some of this information with us. For example, we receive Personal Information from
              vendors to coordinate loads, identify prospective customers and to improve our business
              operations.
            </li>
            <li>
              <strong>Information Providers:</strong> We often purchase, lease or otherwise obtain lists or
              data from third-party list or data providers to populate our load inventory and to correct or
              supplement Personal Information we collect.
            </li>
            <li>
              <strong>Social Media Partners:</strong> Some of our online Services allow you to sign in and
              associate your social media accounts with us, including, but not limited to, Twitter, LinkedIn,
              and Facebook accounts. By using this functionality or otherwise interacting with us through
              social media, you give us permission to access your social media profile, including Personal
              Information that you have made available to be shared, and to use it in accordance with the
              social media partner&rsquo;s terms of use and this Policy. Please refer to the privacy policies
              and privacy settings for your social media account for information about what Personal
              Information is shared with us and other connected applications and to manage the information
              that is shared through your social media account, including information about your activities in
              connection with our Services.
            </li>
            <li>
              <strong>Publicly Available Sources:</strong> We collect Personal Information about you that we
              do not otherwise have (including contact information, employment related information and product
              interest information) from company websites and other publicly available sources in order to
              facilitate our Services.
            </li>
          </ul>
        </div>
      </>
    ),
  },
  {
    number: '2',
    title: 'Use of Personal Information We Collect',
    body: (
      <>
        <p>We use Personal Information we collect to:</p>
        <ul className="m-0 flex flex-col gap-2 pl-6">
          <li>Create and administer your account with us;</li>
          <li>Process your order and facilitate the Services;</li>
          <li>Recommend available loads and drivers;</li>
          <li>
            Send you transaction information, including confirmations, invoices, technical notices, product
            and services information, updates, security alerts, and support and administrative messages;
            Provide you with news about products and services offered by us and our selected partners; Respond
            to your comments and questions and provide customer service;
          </li>
          <li>
            Understand your needs, customize our offerings to those needs, and provide you with better
            service;
          </li>
          <li>
            Conduct analytics and research to train our technology, improve our products, services and
            customer experience and to measure our performance;
          </li>
          <li>Improve our internal operations;</li>
          <li>
            Facilitate the provision of software updates, product support, and other services related to our
            mobile applications and other products;
          </li>
          <li>
            Protect our rights and those of our customers and employees and to prevent, investigate or provide
            notice of fraud or unlawful or criminal activity;
          </li>
          <li>Enforce and carry out contracts and agreements;</li>
          <li>Comply with legal obligations.</li>
        </ul>
      </>
    ),
  },
  {
    number: '3',
    title: 'Disclosure of Personal Information',
    body: (
      <>
        <p>We disclose Personal Information in the following ways:</p>
        <ul className="m-0 flex flex-col gap-2 pl-6">
          <li>
            <strong>Within Cai Labs:</strong> We disclose Personal Information to other Cai Labs entities for
            purposes and uses that are consistent with this Policy. For example, we may disclose Personal
            Information about you to process and fulfill your order or to facilitate communication between you
            and a Cai Labs subsidiary.
          </li>
          <li>
            <strong>Service Providers:</strong> We disclose Personal Information to third-party vendors,
            consultants and other service providers who perform functions or services on our behalf and under
            our instructions to make our Services available to you.
          </li>
          <li>
            <strong>Payment Processing:</strong> We use Stripe for payment, analytics, and other business
            services. Stripe collects identifying information about the devices that connect to its services.
            Stripe uses this information to operate and improve the services it provides to us, including for
            fraud detection. You can learn more about Stripe and read its privacy policy at{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              stripe.com/privacy
            </a>
            .
          </li>
          <li>
            <strong>Brokers and Shippers:</strong> We disclose Personal Information to brokers and shippers as
            part of our Services for purposes of developing and executing proposed and completed transactions
            facilitated by us, as well as to improve performance, products, and services. Brokers and shippers
            also use the Personal Information for their internal purposes (including administrative purposes).
          </li>
          <li>
            <strong>Legal Obligations and Rights:</strong> We may disclose Personal Information:
            <ul className="m-0 flex flex-col gap-2 pl-6">
              <li>in connection with the establishment, exercise, or defense of legal claims;</li>
              <li>to comply with laws or to respond to lawful requests and legal process;</li>
              <li>
                to protect the rights and property of us, our agents, customers, and others, including to
                enforce our agreements, policies, and terms of use;
              </li>
              <li>to detect, suppress, or prevent fraud;</li>
              <li>to reduce credit risk and collect debts owed to us;</li>
              <li>to protect the health and safety of us, our customers, or any person;</li>
              <li>or as otherwise required by applicable law.</li>
            </ul>
          </li>
          <li>
            <strong>Business Transaction or Reorganization:</strong> We may disclose Personal Information as
            part of a corporate business transaction, such as in connection with or during negotiation of any
            merger, sale of company assets, financing, or acquisition of all or a portion of our business. We
            may transfer Personal Information to a third party as one of the business assets in such a
            transaction. We may also disclose Personal Information in the event of insolvency, bankruptcy, or
            receivership.
          </li>
          <li>
            <strong>Consent:</strong> We may disclose Personal Information about you to other third parties
            with your prior consent.
          </li>
        </ul>
      </>
    ),
  },
  {
    number: '4',
    title: 'SMS / Text Messaging and Notifications',
    body: (
      <>
        <p>
          Among the information that we collect and use in connection with our Services are various forms of
          personal information that we use to communicate with you via messaging platforms that we may
          ourselves facilitate (e.g., notifications through our app) or that are facilitated by third-party
          service providers that we engage (collectively, these platforms are a &ldquo;Messaging
          Service&rdquo;).
        </p>
        <p>
          By using the Messaging Service, you agree to the terms of this Policy as it relates specifically to
          the Messaging Service. Cai Labs reserves the right, in our sole discretion, to modify or change this
          Policy, and we will only provide notice to you as required under applicable law. This Policy, and
          any changes, are effective as soon as posted and supersede any prior Policies. Your continued use of
          the Messaging Service following the posting of any changes to the Policy constitutes your full
          acceptance of those changes. We encourage you to check back from time to time and to review this
          Policy.
        </p>
        <p>
          <strong>Collection of Information:</strong> Through your use of the Messaging Service, we will
          receive personal information that individually identifies you, such as your mobile phone number you
          provided when signing up for the Messaging Service, identifying details about your phone, your phone
          service provider (e.g., carrier), etc., any user or screen name that you select in connection with
          the Messaging Service, any comments or feedback regarding the Messaging Service that you send to us,
          and any other information that you choose to include in messages you send through the Messaging
          Service. When you send messages via the Messaging Service, we and our third-party messaging and
          support service providers will also collect your messaging history and any information included in
          those messages.
        </p>
        <p>
          We may also collect personal information about you using cookies or similar technologies. Cookies
          are pieces of information that are stored by your browser on the hard drive or memory of your
          device. Cookies enable personalization of your experience on the Messaging Service.
        </p>
        <p>
          As you utilize the Messaging Service, we may collect additional basic contact information and any
          other information you choose to provide in connection with these activities. We will also collect
          your contact information if you contact us with questions about the Messaging Service or for
          customer service.
        </p>
        <p>
          We may collect personal information about you through our other interactions with you, outside of
          the Messaging Service. For example, when you sign up with FuelApp through our website or use our app
          to provide services to us, we may collect information that we may import into our Messaging Service.
          Please see the rest of our Privacy Policy for more information.
        </p>
        <p>
          In addition to our own collection of information, we engage third party service providers to
          facilitate the Messaging Service, and they may also collect personal information about you as you
          interact with the Messaging Service.
        </p>
        <p>
          <strong>Use of Information:</strong> We use the Messaging Service to communicate with you about the
          freight hauling services that you provide to us, the Services that we provide to you, marketing
          promotions and opportunities that we may wish to offer you. Further, we use personal information to
          deliver, analyze, maintain and support the Messaging Service. We may also use personal information
          to enhance the Messaging Service features and customize and personalize your experiences on the
          Messaging Service. We also use and disclose your information as necessary to respond to subpoenas,
          court orders, or other legal processes or to establish or exercise our legal rights or defend
          against legal claims.
        </p>
        <p>
          <strong>Sharing of Information:</strong> Notwithstanding anything stated to the contrary elsewhere
          in this Privacy Policy, Cai Labs may provide your personal information related to the Messaging
          Service to third-party messaging service or support service providers for the sole purposes of
          processing personal information on our behalf or providing the Messaging Service to you. Such
          information sharing is subject to agreements that oblige those parties to process such information
          only on our instructions and in compliance with this Policy and appropriate confidentiality and
          security measures. Cai Labs engages third-party service providers to facilitate the Messaging
          Service, and these providers are contractually obligated to comply with applicable law. While Cai
          Labs ensures that these third parties follow our privacy standards, we are not responsible for any
          security breaches or unauthorized access caused by third-party platforms. No mobile information will
          be shared with third parties/affiliates for marketing/promotional purposes. All the above categories
          of shared information exclude text messaging originator opt-in data and consent; this information
          will not be shared with any third parties.
        </p>
        <p>
          Otherwise, sharing of information related to the Messaging Service is done according to this Privacy
          Policy.
        </p>
        <p>
          <strong>Protection of Information:</strong> Cai Labs takes a variety of physical, technical,
          administrative, and organizational security measures based on the sensitivity of the information we
          collect designed to protect your personal information against accidental or unlawful destruction or
          accidental loss, alteration, unauthorized disclosure or access. Unfortunately, no online activity
          can be guaranteed to be 100% secure. You should note that in using the Messaging Service, your
          information will travel through third-party infrastructures which are not under our control (such as
          a third-party provider&rsquo;s SMS delivery platform or your carrier network). While we strive to
          take appropriate measures designed to protect your information against unauthorized use or
          disclosure, we cannot ensure or warrant the security of any information you provide. By using the
          Messaging Service, you agree that Cai Labs is not liable for any unintentional disclosure.
        </p>
        <p>
          <strong>Retention of Information:</strong> We retain your personal information for as long as you
          participate in the Messaging Service or as needed to comply with applicable legal obligations. We
          will also retain and use your personal information as necessary to resolve disputes, protect our
          interests and our customers, and enforce our agreements. Once it is no longer needed for these
          purposes, we will securely delete or anonymize it in line with our internal policies. If you are a
          resident of California, you have certain rights regarding your personal information under the
          California Consumer Privacy Act (CCPA). Please see our California Privacy Notice for more
          information.
        </p>
        <p>
          <strong>Choices and Controls:</strong> Cai Labs relies on the Messaging Service to communicate with
          you, and you consented to use the Messaging Service as part of our agreements with you. While you
          consent to receive messages sent using an automatic telephone dialing system, the foregoing shall
          not be interpreted to suggest or imply that any or all of our mobile messages are sent using an
          automatic telephone dialing system. You may choose not to rely on the Messaging Service, but doing
          so may hinder your ability to provide services successfully to Cai Labs.
        </p>
        <p>
          You may opt-out of receiving all text messages from us (including informational or transactional
          messages) by replying with the word &ldquo;STOP&rdquo;, &ldquo;END&rdquo;, &ldquo;CANCEL&rdquo;,
          &ldquo;UNSUBSCRIBE&rdquo;, OR &ldquo;QUIT&rdquo; to a text message from us; however, you acknowledge
          that opting out of receiving all text messages will prohibit us from contacting you with necessary
          Services-related messages including Access Codes, and as such, opting out of receiving all text
          messages will suspend your use of the Services until you contact us at{' '}
          <a href="mailto:support@fuel.app">support@fuel.app</a> to reinstate our ability to text you Access
          Codes. You may also opt out by emailing <a href="mailto:support@fuel.app">support@fuel.app</a>.
        </p>
        <p>
          Consent to receive marketing or promotional messages via the Messaging Service is not a condition of
          providing freight hauling services to Cai Labs. You may opt-out of receiving all marketing and
          promotional messages at any time by replying &ldquo;STOP Ads&rdquo;, &ldquo;END&rdquo;,
          &ldquo;CANCEL&rdquo;, &ldquo;UNSUBSCRIBE&rdquo;, OR &ldquo;QUIT&rdquo; to any text message from us
          or by contacting us at <a href="mailto:support@fuel.app">support@fuel.app</a>.
        </p>
        <p>
          Note that, in any event, even if you opt out of receiving Messaging Services and/or promotional
          communications from us, you may still receive administrative communications from us with respect to
          the Services.
        </p>
        <p>
          <strong>Your Wireless Plan:</strong> As always, message and data rates may apply for any messages
          sent to and by you. Message frequencies may vary. If you have any questions about your text plan or
          data plan, it is best to contact our wireless provider. You represent that you are the account
          holder for the mobile telephone number that you provide when enrolling in our Messaging Services. If
          you change or deactivate that number, please let us know at{' '}
          <a href="mailto:support@fuel.app">support@fuel.app</a>. Neither we, our vendors, nor any mobile
          carrier are liable for delayed or undelivered messages.
        </p>
        <p>
          <strong>Participation Subject to Termination or Change:</strong> We reserve the right to modify or
          discontinue, temporarily or permanently, all or any part of these messages and/or the Messaging
          Services, for any or no reason and with or without notice to you.
        </p>
        <p>
          <strong>Contact Customer Care:</strong> If you experience problems with the Messaging Service,
          please contact us at <a href="mailto:support@fuel.app">support@fuel.app</a> with details about your
          problem and/or your request for support.
        </p>
      </>
    ),
  },
  {
    number: '5',
    title: 'Your Privacy Choices',
    body: (
      <>
        <p>
          If you have opted in to our marketing communications (or where permitted by law, if you have
          provided us or we have obtained your contact information), we may call you, send you email messages,
          direct mail offers, text messages, push notifications or other communications regarding products or
          services depending on the method of communication selected. You may ask us not to do so by:
        </p>
        <ul className="m-0 flex flex-col gap-2 pl-6">
          <li>following the opt-out instructions in promotional communications;</li>
          <li>checking certain boxes on the forms we use to collect your information;</li>
          <li>modifying your communication preferences through the Services or on your device.</li>
        </ul>
        <p>
          To manage any phone, email or text messaging options as a broker or shipper, please contact us at{' '}
          <a href="mailto:support@fuel.app">support@fuel.app</a>. You may also make requests about your
          contact preferences and changes to your information by contacting your primary Cai Labs contact. If
          you opt out of promotional communications, we may still send you non-promotional communications,
          such as transactional information about your account or our ongoing business engagements.
        </p>
        <p>
          If you have permitted access to your location information through your mobile device, you may
          withdraw this permission by modifying your preferences through your device. Please note that you may
          not be able to use certain aspects of the Services, such as nearby load matching without granting
          permission to your location.
        </p>
      </>
    ),
  },
  {
    number: '6',
    title: 'Region-Specific Disclosures',
    body: (
      <>
        <p>
          The laws of certain regions require specific information to be included in a privacy policy. Please
          see below for region-specific disclosures that may be applicable to you:
        </p>
        <p>
          If you are a resident of California, please click here for a supplemental California Privacy Notice.
          If you are a resident of Nevada, you may opt out of &ldquo;sales&rdquo; where your personal
          information is exchanged for monetary consideration. We do not engage in such activity, however, you
          may submit a request to opt out of potential future sales under Nevada law by emailing us at{' '}
          <a href="mailto:support@fuel.app">support@fuel.app</a>. Please include sufficient information for us
          to identify you in your email, such as information about your history with Cai Labs, or, if
          applicable, your account information. Please note we will take reasonable steps to verify your
          identity and the authenticity of the request. Once verified, we will maintain your request in the
          event our practices change.
        </p>
      </>
    ),
  },
  {
    number: '7',
    title: "Children's Privacy",
    body: (
      <p>
        Our online Services are general audience sites intended for corporate and governmental purchasers. Our
        Services are not directed to, and we do not intend to, or knowingly, collect or solicit Personal
        Information from children under the age of 16. If you are under the age of 16, do not use our Services
        or provide us with any Personal Information either directly or by other means. If a child under 16 has
        provided Personal Information to us, we encourage the child&rsquo;s parent or guardian to contact us
        to request that we remove the Personal Information from our systems. If we learn that any Personal
        Information we collect has been provided by a child under the age of 16, we will promptly delete that
        Personal Information.
      </p>
    ),
  },
  {
    number: '8',
    title: 'Third Party Websites',
    body: (
      <p>
        Except where we post, link to or expressly adopt or refer to this Policy, this Policy does not apply
        to, and we are not responsible for, any Personal Information practices of third-party websites and
        online services or the practices of other third parties. To learn about the Personal Information
        practices of third parties, please visit their respective privacy policies.
      </p>
    ),
  },
  {
    number: '9',
    title: 'Changes to This Policy',
    body: (
      <p>
        We will update this Policy from time to time. When we make changes to the Policy, we will change the
        &ldquo;Last Updated&rdquo; date at the beginning of the Policy. If we make material changes to the
        Policy, we will notify you by email to your registered email address, by prominent posting on our
        Services, or through other appropriate communication channels. All changes shall be effective from the
        date of publication unless otherwise provided in the notification.
      </p>
    ),
  },
  {
    number: '10',
    title: 'How to Contact Us',
    body: (
      <>
        <p>
          If you have any questions or requests in connection with this Policy or other privacy-related
          matters, please send an email to <a href="mailto:support@fuel.app">support@fuel.app</a>.
        </p>
        <p>Alternatively, inquiries may be addressed to:</p>
        <p>Cai Labs, Inc.</p>
      </>
    ),
  },
]
